import React from "react";
import styled from "styled-components";
import { COLORS } from "../../../helpers/designConstants";

// Helper function to convert Firestore timestamp to Date
const convertTimestampToDate = (timestamp) => {
  if (!timestamp) return null;
  
  // If it's already a Date object
  if (timestamp instanceof Date) return timestamp;
  
  // If it has a toDate method (Firestore Timestamp)
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // If it's a serialized Firestore timestamp with _seconds
  if (timestamp._seconds !== undefined) {
    return new Date(timestamp._seconds * 1000);
  }
  
  // If it's a string or number timestamp
  return new Date(timestamp);
};

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
    max-width: 100%;
  }
  
  @media (max-width: 375px) {
    gap: 6px;
  }
`;

const StatCard = styled.div`
  background: ${props => props.bgColor || '#f8fafc'};
  border: 1px solid ${props => props.borderColor || COLORS.border};
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    max-width: 100%;
  }
  
  @media (max-width: 375px) {
    padding: 8px;
  }
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.color || COLORS.textPrimary};
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
    margin-bottom: 4px;
  }
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${COLORS.textSecondary};
  font-weight: 500;
  word-wrap: break-word;
  
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const StatSubtext = styled.div`
  font-size: 12px;
  color: ${COLORS.textSecondary};
  margin-top: 4px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  @media (max-width: 480px) {
    font-size: 10px;
    margin-top: 2px;
  }
`;

const LoadingCard = styled(StatCard)`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;

  @keyframes loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const formatNumber = (num) => {
  if (num == null) return 'N/A';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
};

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return 'N/A';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

const formatDuration = (ms) => {
  if (!ms) return 'N/A';
  
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

const calculateSuccessRate = (stats) => {
  if (!stats || !stats.totalExecutions) return 0;
  const successCount = stats.successCount || 0;
  return Math.round((successCount / stats.totalExecutions) * 100);
};

const StatsCards = ({ stats, isLoading }) => {
  // Show loading cards when no stats available OR when explicitly loading (e.g., during manual run)
  if (!stats || isLoading) {
    return (
      <StatsGrid>
        {[1, 2, 3, 4, 5, 6].map(i => (
          <LoadingCard key={i}>
            <StatValue style={{ color: 'transparent' }}>000</StatValue>
            <StatLabel style={{ color: 'transparent' }}>Loading...</StatLabel>
          </LoadingCard>
        ))}
      </StatsGrid>
    );
  }

  const successRate = calculateSuccessRate(stats);
  const avgDuration = stats.totalExecutions > 0 
    ? Math.round(stats.averageDuration / stats.totalExecutions)
    : 0;

  return (
    <StatsGrid>
      <StatCard
        bgColor="#ecfdf5"
        borderColor="#10b981"
      >
        <StatValue color="#059669">
          {formatNumber(stats.totalExecutions || 0)}
        </StatValue>
        <StatLabel>Total Executions</StatLabel>
        <StatSubtext>
          {stats.lastUpdated 
            ? `Since ${convertTimestampToDate(stats.lastUpdated)?.toLocaleDateString() || 'Unknown'}`
            : 'All time'
          }
        </StatSubtext>
      </StatCard>

      <StatCard
        bgColor={successRate >= 95 ? "#ecfdf5" : successRate >= 80 ? "#fef3c7" : "#fef2f2"}
        borderColor={successRate >= 95 ? "#10b981" : successRate >= 80 ? "#f59e0b" : "#ef4444"}
      >
        <StatValue 
          color={successRate >= 95 ? "#059669" : successRate >= 80 ? "#d97706" : "#dc2626"}
        >
          {successRate}%
        </StatValue>
        <StatLabel>Success Rate</StatLabel>
        <StatSubtext>
          {stats.successCount || 0} successful, {stats.failedCount || 0} failed
        </StatSubtext>
      </StatCard>

      <StatCard
        bgColor="#eff6ff"
        borderColor="#3b82f6"
      >
        <StatValue color="#2563eb">
          {formatDuration(avgDuration)}
        </StatValue>
        <StatLabel>Avg Duration</StatLabel>
        <StatSubtext>
          Last: {formatDuration(stats.lastDuration)}
        </StatSubtext>
      </StatCard>

      <StatCard
        bgColor={stats.lastStatus === 'success' ? "#ecfdf5" : "#fef2f2"}
        borderColor={stats.lastStatus === 'success' ? "#10b981" : "#ef4444"}
      >
        <StatValue 
          color={stats.lastStatus === 'success' ? "#059669" : "#dc2626"}
          style={{ fontSize: '18px', textTransform: 'capitalize' }}
        >
          {stats.lastStatus || 'Unknown'}
        </StatValue>
        <StatLabel>Last Status</StatLabel>
        <StatSubtext>
          {stats.lastUpdated 
            ? convertTimestampToDate(stats.lastUpdated)?.toLocaleString() || 'Unknown'
            : 'Never'
          }
        </StatSubtext>
      </StatCard>

      {/* API Response Size Cards */}
      {stats.lastApiResponseSizes && (
        <>
          <StatCard
            bgColor="#f0f9ff"
            borderColor="#0ea5e9"
          >
            <StatValue color="#0284c7">
              {formatBytes(stats.lastApiResponseSizes.totalBytes)}
            </StatValue>
            <StatLabel>Last API Response</StatLabel>
            <StatSubtext>
              Total data downloaded
            </StatSubtext>
          </StatCard>

          <StatCard
            bgColor="#fefce8"
            borderColor="#eab308"
          >
            <StatValue color="#ca8a04">
              {formatBytes(stats.totalBytesDownloaded)}
            </StatValue>
            <StatLabel>Total Downloaded</StatLabel>
            <StatSubtext>
              All-time data transfer
            </StatSubtext>
          </StatCard>

          {/* Detailed breakdown cards */}
          {stats.lastApiResponseSizes.activeRegions && (
            <StatCard
              bgColor="#f0fdf4"
              borderColor="#22c55e"
            >
              <StatValue color="#16a34a">
                {formatBytes(stats.lastApiResponseSizes.activeRegions)}
              </StatValue>
              <StatLabel>ActiveRegions</StatLabel>
              <StatSubtext>
                Product catalog data
              </StatSubtext>
            </StatCard>
          )}

          {stats.lastApiResponseSizes.inventory && (
            <StatCard
              bgColor="#fdf2f8"
              borderColor="#ec4899"
            >
              <StatValue color="#db2777">
                {formatBytes(stats.lastApiResponseSizes.inventory)}
              </StatValue>
              <StatLabel>Inventory</StatLabel>
              <StatSubtext>
                Stock quantity data
              </StatSubtext>
            </StatCard>
          )}
        </>
      )}
    </StatsGrid>
  );
};

export default StatsCards;